import {HTMLAttributes, useEffect, useRef} from 'react';
import {t} from '../../../../common/getTranslations';
import {openWindow} from '../../../../common/openWindow.function';
import {requestContext} from '../../../../common/requestContext';
import {getFormattedNumberWithSuffix} from '../../../../common/shared/utils/getFormattedNumberWithSuffix';
import {twMerge} from '../../../../common/twMerge';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {getRatingText} from '../../../../common/util/getRatingText';
import {ContentOverview} from '../../../../design-system/organisms/ContentOverview/ContentOverview';
import {ContentOverviewBackgroundImage} from '../../../../design-system/organisms/ContentOverviewBackgroundImage/ContentOverviewBackgroundImage';
import {ContentOverviewInfo} from '../../../../design-system/organisms/ContentOverviewInfo/ContentOverviewInfo';
import {ContentOverviewThumbnail} from '../../../../design-system/organisms/ContentOverviewThumbnail/ContentOverviewThumbnail';
import {useDialog} from '../../../../dialog/useDialog.hook';
import {SaleMethod, SaleState} from '../../../../graphql/generated/types';
import {
  MOBILE_CONTENT_HOME_HEADER_ICON_CLASSNAME,
  MOBILE_CONTENT_HOME_HEADER_ID,
  MOBILE_HEADER_HEIGHT_PX,
} from '../../../../header/constants';
import {showImagePopup} from '../../../../imagePopup/imagePopupHandler';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {theme} from '../../../../theme';
import {useLogin} from '../../../../user/useLogin.hook';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useContentMyTicketQuery} from '../../../../wrapper/ticketWrapper';
import {getDisplayWaitfreePeriodByMinute} from '../history/ticket/util/getDisplayWaitfreePeriodByMinute';
import {useContentHomeLikeAndAlarm} from '../hook/useContentHomeLikeAndAlarm';
import {setIsHeaderIconStatic} from '../state/contentHomeSlice';

export interface ContentOverviewContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
}

export function ContentOverviewContainer({
  seriesId,
  ...props
}: ContentOverviewContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const {isLogin} = useLogin();
  const dimmedRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useTypedDispatch();
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data
    ?.contentHomeOverview;
  const isCashTypeHome = data?.content?.saleMethod === SaleMethod.Cash;
  const contentMyTicket = useContentMyTicketQuery(
    {
      seriesId,
      includeWaitfree: true,
    },
    {enabled: isLogin && !!data && !isCashTypeHome}
  ).data?.contentMyTicket;

  const {
    isLikeOn,
    isAlarmOn,
    showArea,
    onClickLikeSeries,
    onClickAlarmOnSeries,
  } = useContentHomeLikeAndAlarm(seriesId);

  const displayPeriodByMinute = getDisplayWaitfreePeriodByMinute({
    chargedPeriodByMinute: contentMyTicket?.waitfree?.chargedPeriodByMinute,
    waitfreePeriodByMinute: data?.content?.waitfreePeriodByMinute,
  });

  const {DialogWrapper, Dialog, hideDialog, showDialog} = useDialog({
    isMobile: requestContext.requestInfo.display.isMobile,
  });

  const onClickCashTypeContentHistory = () => {
    openWindow({
      url: `https://${window.location.host}/content/${seriesId}/history/buy`,
      target: t('contenthome_cash_history_button'),
      features: 'width=460,height=700',
    });
  };

  useEffect(() => {
    dispatch(setIsHeaderIconStatic(true));
    return () => {
      dispatch(setIsHeaderIconStatic(false));
    };
  }, [dispatch]);

  useEffect(() => {
    const mobileContentHomeHeader = document.getElementById(
      MOBILE_CONTENT_HOME_HEADER_ID
    );
    const mobileContentHomeHeaderIcons = document.getElementsByClassName(
      MOBILE_CONTENT_HOME_HEADER_ICON_CLASSNAME
    ) as HTMLCollectionOf<HTMLElement>;
    const dimmedArea = dimmedRef.current;

    if (!data || !dimmedArea || !mobileContentHomeHeader) {
      return;
    }

    // 스크롤 시 상단 영역을 서서히 Fade-Out 시켜주다가, 헤더와 상단 탭 사이의 간격이 40px일 때 완전히 영역을 숨겨주는 애니메이션을 구현합니다.
    const fadeOutOnScroll = () => {
      const dimmedAreaBottom = dimmedArea.getBoundingClientRect().bottom;
      const elementHeight = dimmedArea.offsetHeight;

      const nextOpacity = getDimmedAreaOpacity({
        elementHeight,
        dimmedAreaBottom,
      });

      requestAnimationFrame(() => {
        mobileContentHomeHeader.style.backgroundColor =
          dimmedAreaBottom <= 40 || nextOpacity === 1
            ? theme.colors['theme-bg-base']
            : 'transparent';
        for (const icon of mobileContentHomeHeaderIcons) {
          icon.style.filter = `invert(${nextOpacity * 100}%)`;
        }
        dimmedArea.style.opacity =
          dimmedAreaBottom <= 40
            ? '1'
            : String(getDimmedAreaOpacity({elementHeight, dimmedAreaBottom}));
      });
    };

    window.addEventListener('scroll', fadeOutOnScroll);
    fadeOutOnScroll();

    return () => {
      window.removeEventListener('scroll', fadeOutOnScroll);
    };
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div
      className={twMerge(
        'relative',
        !isMobile && 'overflow-hidden rounded-12pxr',
        isMobile && 'pb-15pxr'
      )}
    >
      {isMobile && (
        <ContentOverviewBackgroundImage
          isMobile={isMobile}
          thumbnailImageSrc={
            data?.content?.thumbnail
              ? `${data?.content?.thumbnail}/dims/resize/384`
              : ''
          }
        />
      )}
      <ContentOverview
        thumbnailImageSrc={
          data?.content?.thumbnail
            ? `${data?.content?.thumbnail}/dims/resize/384`
            : ''
        }
        isMobile={isMobile}
        {...props}
      >
        <ContentOverviewThumbnail
          {...putTiaraObjectProps({
            click: {layer1: '썸네일'},
          })}
          onClickThumbnail={() =>
            showImagePopup({
              imageList: [data?.content?.thumbnail || ''],
              isMobile,
              style: {cursor: 'pointer'},
            })
          }
          isMobile={isMobile}
          src={
            data?.content?.thumbnail
              ? `${data?.content?.thumbnail}/dims/resize/384`
              : ''
          }
          badgeProps={{
            displayPeriodByMinute,
            bm: data?.content?.bm,
            waitfreePeriodByMinute: data?.content?.waitfreePeriodByMinute,
            chargedAt: contentMyTicket?.waitfree?.chargedAt
              ? new Date(contentMyTicket?.waitfree?.chargedAt)
              : undefined,
            isChargedComplete: contentMyTicket?.waitfree?.chargedComplete,
            chargedPeriodByMinute:
              contentMyTicket?.waitfree?.chargedPeriodByMinute,
            baseDate: contentMyTicket?.waitfree?.baseDate
              ? new Date(contentMyTicket?.waitfree?.baseDate)
              : undefined,
            saleState: data.content?.saleState || SaleState.Unknown,
            saleMethod: data.content?.saleMethod || SaleMethod.Ticket,
            discountRateText: data.content?.discountRateText,
          }}
        />
      </ContentOverview>
      <ContentOverviewInfo
        seriesId={seriesId}
        title={data.content?.title}
        author={data.content?.authors}
        publishPeriod={data.content?.pubPeriod}
        onIssue={data.content?.onIssue}
        pageViewCountText={
          data.content?.serviceProperty?.viewCount
            ? getFormattedNumberWithSuffix(
                data.content.serviceProperty?.viewCount
              )
            : '0'
        }
        ratingText={
          data.content?.serviceProperty?.ratingCount &&
          data.content?.serviceProperty?.ratingSum
            ? getRatingText({
                ratingSum: data.content.serviceProperty.ratingSum,
                ratingCount: data.content.serviceProperty.ratingCount,
              })
            : undefined
        }
        categoryType={data.content?.categoryType}
        categoryDisplayName={data.content?.category || '-'}
        subCategory={
          data.content?.subcategory === '채팅소설'
            ? undefined
            : data.content?.subcategory
        }
        isMobile={isMobile}
        isLikeOn={isLikeOn}
        isAlarmOn={isAlarmOn}
        showLikeAndAlarmArea={showArea}
        onClickLike={onClickLikeSeries}
        onClickAlarm={onClickAlarmOnSeries}
        className={isMobile ? 'mt-18pxr' : 'mt-24pxr'}
        isCashTypeHome={isCashTypeHome}
        onClickCashTypeMore={showDialog}
        isLogin={isLogin}
      />
      {isMobile && (
        <div
          ref={dimmedRef}
          className="absolute top-0 left-0 w-full h-full opacity-0 pointer-events-none bg-theme-bg-base"
        />
      )}
      <DialogWrapper
        isUsingScrollBlock={false}
        {...putTiaraObjectProps({
          click: {layer1: '더보기'},
          customProps: {popup_type: '더보기팝업'},
        })}
      >
        <Dialog.Dimmed />
        <Dialog.List>
          <Dialog.Option
            onClick={e => {
              LOG_CLICK_EVENT(
                '더보기팝업_콘텐츠구매내역_클릭',
                e,
                onClickCashTypeContentHistory
              );
            }}
          >
            {t('contenthome_cash_history_button')}
          </Dialog.Option>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button
            onClick={e =>
              LOG_CLICK_EVENT('더보기팝업_취소_클릭', e, hideDialog)
            }
          >
            {t('common_cancel_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </div>
  );
}

function getDimmedAreaOpacity({
  elementHeight,
  dimmedAreaBottom,
}: {
  elementHeight: number;
  dimmedAreaBottom: number;
}) {
  return Math.min(
    (elementHeight - dimmedAreaBottom) /
      (elementHeight - 40 - MOBILE_HEADER_HEIGHT_PX),
    1
  );
}
